import { useCallback, useEffect, useMemo } from 'react';

import { useSyncedPref } from '#hooks/useSyncedPref';

import {
  escribirWidgets,
  incorporarNuevos,
  leerVistos,
  leerWidgets,
  todosVistos,
} from './modeloWidgets';
import type { WidgetInicio } from './modeloWidgets';

/**
 * Widgets del inicio (orden y tamaño), en la preferencia sincronizada
 * `inicio-widgets` para que el inicio sea igual en todos los dispositivos.
 * Un widget nuevo (p. ej. «Copia de seguridad») se añade una vez a la lista
 * ya guardada; `inicio-widgets-vistos` recuerda que ya se añadió.
 */
export function useInicioWidgets() {
  const [raw, setRaw] = useSyncedPref('inicio-widgets');
  const [rawVistos, setRawVistos] = useSyncedPref('inicio-widgets-vistos');
  const widgets = useMemo(() => leerWidgets(raw), [raw]);
  const vistos = useMemo(() => leerVistos(rawVistos), [rawVistos]);

  // Solo con una lista guardada: sin ella ya sale el orden por defecto.
  useEffect(() => {
    if (raw == null || raw === '') {
      return;
    }
    const r = incorporarNuevos(widgets, vistos);
    if (r) {
      setRaw(escribirWidgets(r.lista));
      setRawVistos(JSON.stringify(r.vistos));
    }
  }, [raw, widgets, vistos, setRaw, setRawVistos]);

  const marcarVistos = useCallback(() => {
    const todos = todosVistos(vistos);
    if (todos.length !== vistos.length) {
      setRawVistos(JSON.stringify(todos));
    }
  }, [vistos, setRawVistos]);

  const guardar = useCallback(
    (lista: readonly WidgetInicio[]) => {
      marcarVistos();
      setRaw(escribirWidgets(lista));
    },
    [marcarVistos, setRaw],
  );
  /** Vuelve al orden por defecto (borra la preferencia). */
  const restablecer = useCallback(() => {
    marcarVistos();
    setRaw('');
  }, [marcarVistos, setRaw]);
  return { widgets, guardar, restablecer };
}
